import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { CaptivePortalProps } from './common';
import LoadingButton from '@shared/components/LoadingButton';
import { useGlobalState } from '@shared/components/common/GlobalStateContext';
import { useTranslation } from 'react-i18next';

const validateEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export function D3Captive(props: CaptivePortalProps) {
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const { notifyError } = useGlobalState();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    if (props.isLoading) {
      return;
    }
    e.preventDefault();
    if (!validateEmail(email)) {
      return notifyError(t('pleaseEnterValidEmail', 'Please enter a valid email address'));
    }
    if (!agreed) {
      return notifyError(t('pleaseAcceptTerms', 'You must agree to the terms of service'));
    }

    await props.login({
      email,
      captivePortal: props.data,
    });
  };

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="80vh"
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 500,
            borderRadius: 2,
          }}
        >
          <Box display="flex" justifyContent="center" mb={3}>
            <img src="/logo_on_white.png" alt="D3 Logo" style={{ maxHeight: 60, width: 'auto' }} />
          </Box>

          <Typography variant="h5" align="center" gutterBottom fontWeight="500">
            {t('welcomeToNetwork', 'Welcome to the Network')}
          </Typography>

          <Typography variant="body1" align="center" paragraph sx={{ mb: 4 }}>
            {t(
              'connectionInstructions',
              'To connect to the internet, please provide your email and accept the terms of service.',
            )}
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              required
              label="Email Address"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 3 }}
            />

            <FormControlLabel
              control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  {t('agreeToTerms', 'I agree to the')}{' '}
                  <a href="#" style={{ textDecoration: 'none' }}>
                    {t('termsOfService', 'Terms of Service')}
                  </a>{' '}
                  {t('and', 'and')}{' '}
                  <a href="#" style={{ textDecoration: 'none' }}>
                    {t('privacyPolicy', 'Privacy Policy')}
                  </a>
                </Typography>
              }
              sx={{ mb: 3 }}
            />

            <LoadingButton
              onClick={handleSubmit}
              loading={props.isLoading}
              variant="contained"
              color="primary"
              fullWidth
              size="large"
            >
              {t('connectToInternet', 'Connect to Internet')}
            </LoadingButton>
          </form>
        </Paper>

        <Typography variant="caption" sx={{ mt: 2, opacity: 0.7 }}>
          {t('copyright', '© {{year}} D3 Technologies. All rights reserved.', {
            year: new Date().getFullYear(),
          })}
        </Typography>
      </Box>
    </Container>
  );
}
